'use client';
import React, { useState } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { Mail, MessageSquare, Clock, CheckCircle, Send, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

const CATEGORIES = [
  'General Inquiry',
  'Technical Support',
  'Registration Help',
  'Certificate Issue',
  'School Partnership',
  'Billing & Payments',
  'Press & Media',
  'Other',
];

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [form, setForm] = useState<FormData>({ name: '', email: '', subject: '', category: 'General Inquiry', message: '' });
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          to: 'hello@onlineolympiads.org',
          data: {
            senderName: form.name,
            senderEmail: form.email,
            subject: form.subject || `[${form.category}] Contact Form Submission`,
            category: form.category,
            message: form.message,
          },
        }),
      });

      if (res.ok || res.status === 400) {
        setStatus('success');
        setForm({ name: '', email: '', subject: '', category: 'General Inquiry', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or email us directly at hello@onlineolympiads.org');
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Topbar />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="bg-hero-gradient py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-semibold uppercase tracking-widest mb-6">
              <MessageSquare size={12} />
              Contact Us
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              We&apos;re Here to Help
            </h1>
            <p className="text-slate-300 text-base max-w-xl mx-auto">
              Have a question, issue, or partnership inquiry? Our team typically responds within 24 hours.
            </p>
          </div>
        </section>

        <section className="py-14 px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact info */}
            <div className="flex flex-col gap-5">
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Mail size={18} className="text-primary" />
                </div>
                <h3 className="font-bold text-foreground text-sm mb-1">Email Support</h3>
                <p className="text-xs text-muted-foreground mb-2">For general inquiries and support</p>
                <a href="mailto:hello@onlineolympiads.org" className="text-sm text-primary font-semibold hover:underline">
                  hello@onlineolympiads.org
                </a>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                  <MessageSquare size={18} className="text-secondary" />
                </div>
                <h3 className="font-bold text-foreground text-sm mb-1">School Partnerships</h3>
                <p className="text-xs text-muted-foreground mb-2">Bulk registrations and institutional access</p>
                <a href="mailto:schools@onlineolympiads.org" className="text-sm text-secondary font-semibold hover:underline">
                  schools@onlineolympiads.org
                </a>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <Clock size={18} className="text-accent" />
                </div>
                <h3 className="font-bold text-foreground text-sm mb-1">Response Time</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We respond to all inquiries within <strong className="text-foreground">24 hours</strong> on business days. For urgent issues, mention &quot;URGENT&quot; in your subject line.
                </p>
              </div>

              <div className="bg-muted/60 border border-border rounded-2xl p-5">
                <p className="text-xs font-semibold text-foreground mb-2">Common Questions</p>
                <ul className="space-y-2">
                  {['How do I register for an olympiad?', 'Where can I find my certificate?', 'How are scores calculated?', 'Can my school register in bulk?'].map((q) => (
                    <li key={q}>
                      <a href="/blogs" className="text-xs text-muted-foreground hover:text-primary transition-colors">→ {q}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-2xl p-8">
                <h2 className="text-lg font-extrabold text-foreground mb-6">Send Us a Message</h2>

                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center fade-in">
                    <CheckCircle size={40} className="text-green-500 mb-4" />
                    <h3 className="font-bold text-foreground text-lg mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground text-sm max-w-sm">
                      Thank you for reaching out. Our team will get back to you within 24 hours at the email you provided.
                    </p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="mt-6 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Full Name *</label>
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Email Address *</label>
                        <input
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Category</label>
                        <select
                          name="category"
                          value={form.category}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5">Subject</label>
                        <input
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          placeholder="Brief subject line"
                          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Message *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Describe your question or issue in detail…"
                        className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground resize-none"
                      />
                    </div>

                    {status === 'error' && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                        <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">{errorMsg}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={15} />
                      {status === 'loading' ? 'Sending…' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
